import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BookOpen, GraduationCap, Calendar, Layers, ChevronDown, ChevronRight, RefreshCw } from "lucide-react";

const API_URL = '';

function SubjectCard({ subject }) {
  const [expanded, setExpanded] = useState(false);
  const years = Object.entries(subject.years).sort(([a], [b]) => Number(a) - Number(b));
  const totalWeeks = subject.total_plans;

  return (
    <Card
      data-testid={`curriculum-card-${subject.subject_key}`}
      className="border border-slate-200 hover:border-slate-300 transition-colors"
    >
      <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-blue-600" />
            <CardTitle className="text-base font-semibold">{subject.subject_label}</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">
              {years.length} year{years.length > 1 ? "s" : ""}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {totalWeeks} weeks
            </Badge>
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </div>
        </div>
        <div className="flex gap-1 mt-1">
          {years.map(([year]) => (
            <Badge key={year} className="text-xs bg-blue-50 text-blue-700 border-blue-200">
              Yr {year}
            </Badge>
          ))}
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0">
          {years.map(([year, data]) => (
            <div key={year} className="mb-3 last:mb-0">
              <div className="flex items-center gap-2 mb-1">
                <GraduationCap className="h-3.5 w-3.5 text-slate-500" />
                <span className="text-sm font-medium">Year {year}</span>
                <span className="text-xs text-slate-500">
                  {Object.keys(data.terms).length} term{Object.keys(data.terms).length > 1 ? "s" : ""} &middot; {data.total} weeks
                </span>
              </div>
              <div className="ml-5 space-y-0.5">
                {Object.entries(data.terms).sort(([a], [b]) => Number(a) - Number(b)).map(([term, count]) => (
                  <div key={term} className="text-xs text-slate-600">
                    <span className="font-medium text-slate-700">Term {term}:</span>{" "}
                    {count} weekly plans
                  </div>
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      )}
    </Card>
  );
}

export default function CurriculumOverviewPage() {
  const [activeTab, setActiveTab] = useState("all");
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchOverview() {
      try {
        const res = await fetch(`${API_URL}/api/curriculum-data/overview`);
        if (res.ok) setOverview(await res.json());
      } catch (err) {
        console.error('Failed to load curriculum overview:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchOverview();
  }, []);

  const englishSubjects = overview.filter(s => s.school_group === 'english');
  const francophoneSubjects = overview.filter(s => s.school_group === 'francophone');

  const totalSubjects = overview.length;
  const totalPlans = overview.reduce((sum, s) => sum + s.total_plans, 0);
  const groupCount = new Set(overview.map(s => s.school_group)).size;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="curriculum-overview-loading">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-3 text-muted-foreground">Loading curriculum data...</span>
      </div>
    );
  }

  return (
    <div data-testid="curriculum-overview-page" className="space-y-6 p-4 max-w-5xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Curriculum Overview</h1>
        <p className="text-sm text-slate-500 mt-1">
          All available curriculum data across schools and subjects
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 flex items-center gap-3">
            <Layers className="h-8 w-8 text-blue-600" />
            <div>
              <div className="text-2xl font-bold text-blue-900">{totalSubjects}</div>
              <div className="text-xs text-blue-600">Subjects with data</div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4 flex items-center gap-3">
            <Calendar className="h-8 w-8 text-green-600" />
            <div>
              <div className="text-2xl font-bold text-green-900">{totalPlans}</div>
              <div className="text-xs text-green-600">Total weekly plans</div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4 flex items-center gap-3">
            <GraduationCap className="h-8 w-8 text-purple-600" />
            <div>
              <div className="text-2xl font-bold text-purple-900">{groupCount}</div>
              <div className="text-xs text-purple-600">School groups</div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">All Subjects</TabsTrigger>
          <TabsTrigger value="english" data-testid="tab-english">English-Medium</TabsTrigger>
          <TabsTrigger value="francophone" data-testid="tab-francophone">Tagaga</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4 space-y-3">
          {englishSubjects.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-slate-800 mb-2">English-Medium Schools</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {englishSubjects.map(s => <SubjectCard key={s.subject_key} subject={s} />)}
              </div>
            </div>
          )}
          {francophoneSubjects.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-slate-800 mb-2">College de Tagaga (Francophone)</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {francophoneSubjects.map(s => <SubjectCard key={s.subject_key} subject={s} />)}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="english" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {englishSubjects.map(s => <SubjectCard key={s.subject_key} subject={s} />)}
          </div>
        </TabsContent>

        <TabsContent value="francophone" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {francophoneSubjects.map(s => <SubjectCard key={s.subject_key} subject={s} />)}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
